import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { userLsKey } from "@/lib/userScopedStorage";

const DEFAULT_LIGHT_COLORS = {
  background: "hsl(210, 40%, 98%)",
  foreground: "hsl(210, 15%, 15%)",
  card: "hsl(0, 0%, 100%)",
  cardForeground: "hsl(210, 15%, 15%)",
  primary: "hsl(213, 92%, 48%)",
  primaryForeground: "hsl(0, 0%, 100%)",
  secondary: "hsl(210, 40%, 94%)",
  secondaryForeground: "hsl(210, 15%, 30%)",
  accent: "hsl(25, 95%, 55%)",
  accentForeground: "hsl(0, 0%, 100%)",
  muted: "hsl(210, 40%, 94%)",
  mutedForeground: "hsl(210, 15%, 50%)",
  destructive: "hsl(0, 84%, 60%)",
  destructiveForeground: "hsl(0, 0%, 100%)",
  border: "hsl(210, 40%, 88%)",
  input: "hsl(210, 40%, 88%)",
  ring: "hsl(213, 92%, 48%)",
};

const DEFAULT_DARK_COLORS = {
  background: "hsl(0, 0%, 0%)",
  foreground: "hsl(200, 7%, 91%)",
  card: "hsl(228, 10%, 10%)",
  cardForeground: "hsl(0, 0%, 85%)",
  primary: "hsl(203, 87%, 52%)",
  primaryForeground: "hsl(0, 0%, 100%)",
  secondary: "hsl(195, 15%, 95%)",
  secondaryForeground: "hsl(210, 25%, 8%)",
  accent: "hsl(205, 70%, 8%)",
  accentForeground: "hsl(203, 87%, 52%)",
  muted: "hsl(0, 0%, 9%)",
  mutedForeground: "hsl(210, 3%, 46%)",
  destructive: "hsl(356, 90%, 54%)",
  destructiveForeground: "hsl(0, 0%, 100%)",
  border: "hsl(210, 5%, 15%)",
  input: "hsl(207, 27%, 18%)",
  ring: "hsl(202, 89%, 53%)",
};

const THEME_STORAGE_KEY = "goalvia-theme-cache";

interface CachedTheme {
  themeColors: { light: Record<string, string>; dark: Record<string, string> } | null;
  themeFonts: { body?: string; headings?: string } | null;
}

function loadCachedTheme(userId: string | null): CachedTheme | null {
  try {
    const key = userId ? `${THEME_STORAGE_KEY}-${userId}` : THEME_STORAGE_KEY;
    const cached = localStorage.getItem(key);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
  }
  return null;
}

function saveCachedTheme(theme: CachedTheme, userId: string | null) {
  try {
    const key = userId ? `${THEME_STORAGE_KEY}-${userId}` : THEME_STORAGE_KEY;
    localStorage.setItem(key, JSON.stringify(theme));
  } catch (e) {
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { data: preferences, isLoading, isFetched } = useQuery({
    queryKey: ["/api/user/preferences"],
  });

  // Eager load from unscoped key for the initial render (pre-login fallback).
  const cachedThemeRef = useRef<CachedTheme | null>(loadCachedTheme(null));

  // When userId becomes known, reload from the user-scoped cache key.
  useEffect(() => {
    if (userId) {
      const userCache = loadCachedTheme(userId);
      if (userCache) {
        cachedThemeRef.current = userCache;
      }
    }
  }, [userId]);

  useEffect(() => {
    const applyTheme = (themeColors: any, themeFonts: any) => {
      const root = document.documentElement;
      const isDarkMode = root.classList.contains('dark');
      
      const lightColors = themeColors?.light || DEFAULT_LIGHT_COLORS;
      const darkColors = themeColors?.dark || DEFAULT_DARK_COLORS;
      const colors = isDarkMode ? darkColors : lightColors;
      
      Object.entries(colors).forEach(([key, value]) => {
        const cssVar = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
        root.style.setProperty(cssVar, value as string);
      });
      
      if (themeFonts?.body) {
        root.style.setProperty('--font-sans', themeFonts.body);
        document.body.style.fontFamily = themeFonts.body;
      }
      
      if (themeFonts?.headings) {
        const headingTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
        headingTags.forEach(tag => {
          const elements = document.querySelectorAll(tag);
          elements.forEach((el) => {
            (el as HTMLElement).style.fontFamily = themeFonts.headings;
          });
        });
      }
    };

    const applyCurrentTheme = () => {
      // Priority 1: Use preferences from server if available
      if (preferences) {
        const prefs = preferences as any;
        const themeColors = prefs.themeColors;
        const themeFonts = prefs.themeFonts;
        
        // Update cache with server data (user-scoped).
        cachedThemeRef.current = { themeColors, themeFonts };
        saveCachedTheme({ themeColors, themeFonts }, userId);
        
        applyTheme(themeColors, themeFonts);
        return;
      }
      
      // Priority 2: While loading or during tab switches, always use cached theme
      // This prevents the flash to default theme during navigation
      if (cachedThemeRef.current) {
        applyTheme(
          cachedThemeRef.current.themeColors,
          cachedThemeRef.current.themeFonts
        );
        return;
      }
      
      // Priority 3: Only use defaults if we've confirmed there's no saved theme
      // (i.e., query completed and returned no preferences, and no cache exists)
      if (!isLoading && isFetched && !preferences && !cachedThemeRef.current) {
        applyTheme(null, null);
      }
    };
    
    applyCurrentTheme();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          applyCurrentTheme();
        }
      });
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    
    return () => observer.disconnect();
  }, [preferences, isLoading, isFetched]);

  return <>{children}</>;
}
